import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetFinanceClientsQuery } from '../services/user/userApi';

const today = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
};

const firstOfMonth = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-01`;
};

const fmt = (dateStr) => {
  const d = new Date(dateStr);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const FinancePage = () => {
  const navigate = useNavigate();
  const [from, setFrom]   = useState(firstOfMonth());
  const [to, setTo]       = useState(today());
  const [type, setType]         = useState('');
  const [userType, setUserType] = useState('');
  const [page, setPage]         = useState(1);
  const limit = 50;

  // Applied filters (only change on Apply)
  const [applied, setApplied] = useState({ from: firstOfMonth(), to: today(), type: '', userType: '' });

  const { data, isLoading, error } = useGetFinanceClientsQuery({
    from: applied.from,
    to: applied.to,
    type: applied.type,
    userType: applied.userType,
    page,
    limit,
  });

  const entries    = data?.data || [];
  const pagination = data?.pagination || {};
  const summary    = data?.summary || null;
  const totalPages = pagination.totalPages || 1;
  const totalItems = pagination.total || 0;

  const handleApply = () => {
    setPage(1);
    setApplied({ from, to, type, userType });
  };

  const handleReset = () => {
    const f = firstOfMonth(), t = today();
    setFrom(f); setTo(t); setType(''); setUserType('');
    setPage(1);
    setApplied({ from: f, to: t, type: '', userType: '' });
  };

  return (
    <div className="w-full">
      <div className="w-full px-2">
        <div className="bg-white rounded shadow border border-gray-200">

          {/* Header */}
          <div className="bg-[#3e525f] px-5 py-3 flex items-center justify-between">
            <span className="text-xl text-white font-normal">Finance</span>
            <button
              onClick={() => navigate(-1)}
              className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors text-sm"
            >
              Back
            </button>
          </div>

          {/* Filters */}
          <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">From</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 text-sm outline-none focus:border-gray-400"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">To</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 text-sm outline-none focus:border-gray-400"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 text-sm outline-none focus:border-gray-400 bg-white min-w-[110px]"
              >
                <option value="">All</option>
                <option value="DIYA">DIYA</option>
                <option value="LIYA">LIYA</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">User Type</label>
              <select
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 text-sm outline-none focus:border-gray-400 bg-white min-w-[120px]"
              >
                <option value="">All</option>
                <option value="Admin">Admin</option>
                <option value="SuperAdmin">SuperAdmin</option>
                <option value="MiniAdmin">MiniAdmin</option>
                <option value="Master">Master</option>
                <option value="Super">Super</option>
                <option value="Agent">Agent</option>
              </select>
            </div>
            <button
              onClick={handleApply}
              className="px-4 py-1.5 bg-[#3e525f] text-white text-sm hover:bg-[#2d3f4a] transition-colors"
            >
              Apply
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-1.5 border border-gray-300 text-sm hover:bg-gray-100 transition-colors"
            >
              Reset
            </button>
          </div>

          {/* Summary */}
          {!isLoading && summary && (
            <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap gap-3">
              {[
                { key: 'totalDiya',  label: 'Total Diya',  type: 'amount', color: 'text-red-600' },
                { key: 'diyaCount',  label: 'Diya Count',  type: 'count',  color: 'text-red-500' },
                { key: 'totalLiya',  label: 'Total Liya',  type: 'amount', color: 'text-green-600' },
                { key: 'liyaCount',  label: 'Liya Count',  type: 'count',  color: 'text-green-500' },
                { key: 'net',        label: 'Net',         type: 'amount', color: null },
              ].map(({ key, label, type, color }) => {
                const v = summary[key] ?? 0;
                const dynColor = color || (Number(v) < 0 ? 'text-red-600' : 'text-green-600');
                return (
                  <div key={key} className="flex flex-col items-center bg-gray-50 border border-gray-200 rounded px-5 py-2 min-w-[110px]">
                    <span className="text-xs text-gray-500 mb-0.5">{label}</span>
                    <span className={`text-base font-semibold ${dynColor}`}>
                      {type === 'amount'
                        ? `₹${Number(v).toLocaleString('en-IN')}`
                        : Number(v).toLocaleString('en-IN')}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center py-10"><Loading /></div>
            ) : error ? (
              <div className="py-8 text-center text-red-500 text-sm">Error loading data</div>
            ) : (
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#3e525f] text-white">
                    <th className="px-4 py-3 text-left font-medium border-r border-gray-600 w-10">#</th>
                    <th className="px-4 py-3 text-left font-medium border-r border-gray-600 whitespace-nowrap">Date</th>
                    <th className="px-4 py-3 text-left font-medium border-r border-gray-600">Type</th>
                    <th className="px-4 py-3 text-left font-medium border-r border-gray-600">From</th>
                    <th className="px-4 py-3 text-left font-medium border-r border-gray-600">To</th>
                    <th className="px-4 py-3 text-right font-medium border-r border-gray-600">Amount</th>
                    <th className="px-4 py-3 text-left font-medium">Remark</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {entries.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-10 text-center text-gray-400">
                        <div className="flex flex-col items-center gap-2">
                          <svg width="64" height="41" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                            <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                              <ellipse fill="#f5f5f5" cx="32" cy="33" rx="32" ry="7" />
                              <g fillRule="nonzero" fill="#d9d9d9">
                                <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" />
                                <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z" />
                              </g>
                            </g>
                          </svg>
                          <span>No data</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    entries.map((row, i) => (
                      <tr key={row._id} className="border-t border-gray-200 hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500 border-r border-gray-200">{(page - 1) * limit + i + 1}</td>
                        <td className="px-4 py-3 text-gray-600 border-r border-gray-200 whitespace-nowrap text-xs">{fmt(row.date)}</td>
                        <td className="px-4 py-3 border-r border-gray-200">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${row.type === 'DIYA' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                            {row.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 border-r border-gray-200">
                          <div className="font-medium text-gray-800 text-xs">{row.from?.fullName}</div>
                          <div className="text-gray-400 text-xs">{row.from?.userId} · {row.from?.userType}</div>
                        </td>
                        <td className="px-4 py-3 border-r border-gray-200">
                          <div className="font-medium text-gray-800 text-xs">{row.to?.fullName}</div>
                          <div className="text-gray-400 text-xs">{row.to?.userId} · {row.to?.userType}</div>
                        </td>
                        <td className="px-4 py-3 border-r border-gray-200 text-right">
                          <span className={`font-semibold ${Number(row.amount) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            ₹{Number(row.amount).toLocaleString('en-IN')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{row.remark || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && totalPages > 1 && (
            <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between text-sm">
              <span className="text-gray-500">
                {(page - 1) * limit + 1}–{Math.min(page * limit, totalItems)} of {totalItems}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1 border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 bg-[#3e525f] text-white">{page}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1 border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default FinancePage;
